import { GT4SD_CHECKPOINTS, GT4SD_RT_TARGET_RANGE } from '../../config/gt4sdCheckpoints';

// Only the two algorithms bundled in the gt4sd-service image are listed. guacamol,
// reinvent, and moler are stubs in the backend (HTTP 501 algorithm_not_bundled — see
// models/gt4sd-service/main.py _DISPATCH); re-add them here once their deps are bundled.
// GT4SD RT Part D: regression_transformer is listed first because it is the only
// algorithm with a built-in checkpoint. RT-6 removed hugging_face's, so that option
// runs only against customer weights and says so when picked.
const GT4SD_ALGORITHMS = [
  { value: 'regression_transformer', label: 'Regression Transformer (property-conditioned)' },
  { value: 'hugging_face',           label: 'HuggingFace Mol Gen (de-novo, your own weights)' },
];

const [RT_TARGET_MIN, RT_TARGET_MAX] = GT4SD_RT_TARGET_RANGE;
// Measured, not guessed: the acceptance sweep found the model saturates near this
// value when it edits the default seed. Accepting a target is not a claim it is
// reachable, so the number the sweep measured is shown beside the field.
const RT_MEASURED_CEILING = 3.4;

// M1: only what the deployed RT checkpoint (logP-trained SELFIES model) actually
// conditions — offering qed/tpsa/... here produced silently meaningless output.
// Re-add a property together with its checkpoint mapping (see modelRegistry.js note).
const RT_PROPERTIES = [
  { value: 'logP',            label: 'logP' },
];

export default function GT4SDForm({
  algorithm,
  setAlgorithm,
  algorithmVersion,
  setAlgorithmVersion,
  targetSmiles,
  setTargetSmiles,
  targetProperty,
  setTargetProperty,
  targetValue,
  setTargetValue,
  numSamples,
  setNumSamples,
  errors = {},
}) {
  const needsProperty = algorithm === 'regression_transformer';
  const needsSeed     = algorithm === 'regression_transformer';
  // R4-5: the service accepts only allowlisted checkpoints. The first id is
  // the default (sent as an empty algorithm_version); any others are options.
  const checkpoints = GT4SD_CHECKPOINTS[algorithm] || [];
  const extraCheckpoints = checkpoints.slice(1);
  // An algorithm with no built-in checkpoint has nothing to offer and nothing to send:
  // the service takes customer weights as a checkpoint_gcs_uri, never as a version.
  const noBuiltin = checkpoints.length === 0;

  // A checkpoint picked for one algorithm must not ride along to another,
  // where the service would reject it.
  const onAlgorithmChange = (next) => {
    setAlgorithm(next);
    if (algorithmVersion && !(GT4SD_CHECKPOINTS[next] || []).includes(algorithmVersion)) {
      setAlgorithmVersion('');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <label htmlFor="gt4sd-algorithm" style={{ color: '#94a3b8', fontSize: 12 }}>Algorithm</label>
        <select
          id="gt4sd-algorithm"
          value={algorithm}
          onChange={(e) => onAlgorithmChange(e.target.value)}
          style={{ display: 'block', width: '100%' }}
        >
          {GT4SD_ALGORITHMS.map(a => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="gt4sd-checkpoint" style={{ color: '#94a3b8', fontSize: 12 }}>Model checkpoint</label>
        <select
          id="gt4sd-checkpoint"
          value={extraCheckpoints.includes(algorithmVersion) ? algorithmVersion : ''}
          onChange={(e) => setAlgorithmVersion(e.target.value)}
          disabled={noBuiltin}
          style={{ display: 'block', width: '100%' }}
        >
          <option value="">{noBuiltin ? 'None available' : `${checkpoints[0]} (default)`}</option>
          {extraCheckpoints.map(id => (
            <option key={id} value={id}>{id}</option>
          ))}
        </select>
        {noBuiltin && (
          <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>
            This algorithm has no built-in checkpoint: it runs only with your own weights,
            registered as a custom model. For property-conditioned generation out of the box,
            use the Regression Transformer.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={{ color: '#94a3b8', fontSize: 12 }}>Samples</label>
          <input
            type="number"
            value={numSamples}
            onChange={(e) => setNumSamples(e.target.value)}
            min={1}
            max={200}
            style={{ display: 'block', width: '100%', borderColor: errors.numSamples ? '#f87171' : undefined }}
          />
          {errors.numSamples && <div style={{ color: '#f87171', fontSize: 11 }}>{errors.numSamples}</div>}
        </div>
      </div>

      {needsProperty && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="gt4sd-target-property" style={{ color: '#94a3b8', fontSize: 12 }}>Target Property</label>
            <select
              id="gt4sd-target-property"
              value={targetProperty}
              onChange={(e) => setTargetProperty(e.target.value)}
              style={{ display: 'block', width: '100%' }}
            >
              <option value="">— select —</option>
              {RT_PROPERTIES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="gt4sd-target-value" style={{ color: '#94a3b8', fontSize: 12 }}>Target Value</label>
            <input
              id="gt4sd-target-value"
              type="number"
              step="0.01"
              required
              min={RT_TARGET_MIN}
              max={RT_TARGET_MAX}
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              placeholder="e.g. 2.5"
              style={{ display: 'block', width: '100%', borderColor: errors.targetValue ? '#f87171' : undefined }}
            />
            {errors.targetValue && <div style={{ color: '#f87171', fontSize: 11 }}>{errors.targetValue}</div>}
          </div>
        </div>
      )}

      {needsProperty && (
        <div style={{ color: '#94a3b8', fontSize: 11 }}>
          logP from {RT_TARGET_MIN} to {RT_TARGET_MAX}, and required: the model conditions on
          it and has no default. Targets above about {RT_MEASURED_CEILING} are accepted but
          rarely reached, because the model edits a seed molecule rather than building one to
          order. Every molecule comes back with its measured logP, so you can see what it did.
        </div>
      )}

      {needsSeed && (
        <div>
          <label style={{ color: '#94a3b8', fontSize: 12 }}>
            {algorithm === 'moler' ? 'Scaffold SMILES' : 'Seed SMILES (optional)'}
          </label>
          <input
            type="text"
            value={targetSmiles}
            onChange={(e) => setTargetSmiles(e.target.value)}
            placeholder="e.g. c1ccccc1"
            style={{ display: 'block', width: '100%' }}
          />
        </div>
      )}
    </div>
  );
}
